import React, { useState, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { Badge } from '@/components/ui/badge'
import { CopyButton } from '@/components/copy-button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  GetServerInstanceData,
  ServerServiceURL,
} from '@/services/inference.gen'
import {
  Copy as CustomCopyIcon,
  Fan,
  Microchip,
  Loader2,
  HardDrive,
  Activity,
  RefreshCw,
} from 'lucide-react'
import { SpecCard } from '@/components/spec-card'
import { useStorageVolumeInfo } from '@/hooks/useStorageVolumeInfo'
import { GoogleDriveDetails } from '@/pages/DashboardStorage/StorageCard/GoogleDriveDetails'
import { Button } from '@/components/ui/button'
import GoogleDriveLogo from '/assets/image/google-drive.svg'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { cn } from '@/lib/utils'
import {
  useGetServerInstance,
  useGetServerInstanceScaleHistory,
  useUpdateServerInstanceScaleConfig,
  useGetServerWebSSHSession,
} from '@/services/server'
import { useGetServerTemplate } from '@/services/template'
import { ExecutionDetails } from '@/components/ExecutionDetails'
import {
  AutoscalingConfigForm,
  AutoscalingFormData,
} from '@/components/autoscaling-config-form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Form } from '@/components/ui/form'
import toast from 'react-hot-toast'
import { Edit2, Save, X, Terminal, Plus } from 'lucide-react'
import { ServerLogs } from './ServerLogs'
import { ScaleHistoryChart } from './ScaleHistoryChart'
import { MountStorageDialog } from './MountStorageDialog'

// Types
type InfoSectionProps = {
  label: string
  content: React.ReactNode
}

type ServiceUrlInfo = {
  name: string
  url: string
  fullUrl: string
  mappedPort: number | string
}

export type ServerContentProps = {
  serverInstance: GetServerInstanceData
  status?: string
  serverId: number
  activeTab?: 'overview' | 'autoscaling' | 'logs'
  onTabChange?: (tab: 'overview' | 'autoscaling' | 'logs') => void
}

export const ServerDetailContent = ({
  serverInstance,
  serverId,
  activeTab = 'overview',
  onTabChange,
}: ServerContentProps) => {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [mountStorageDialogOpen, setMountStorageDialogOpen] = useState(false)

  const { data: template } = useGetServerTemplate(serverInstance.template_id)

  // Determine actual active tab - if autoscaling is selected but not supported, default to overview
  const resolvedActiveTab =
    activeTab === 'autoscaling' && !template?.support_scale
      ? 'overview'
      : activeTab

  // Use separate query for polling autoscaling data
  const {
    data: polledServerInstance,
    refetch: manualRefetch,
    isRefetching,
    dataUpdatedAt,
  } = useGetServerInstance(serverId, {
    enabled: serverInstance.enable_scale,
    refetchInterval: serverInstance.enable_scale ? 30000 : false, // 30 seconds
  })

  // Use polled data if available, otherwise use prop data
  const currentServerInstance = polledServerInstance || serverInstance

  const getServerWebSSHSession = useGetServerWebSSHSession()

  // Update last updated timestamp when data changes
  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt))
    }
  }, [dataUpdatedAt])

  // Get properties from serverInstance
  const {
    metadata,
    service_urls,
    cpu_limit_count,
    ram_limit_gb,
    gpu_request_count,
    gpu_request_gmem,
    storage_volume_info,
    ephemeral_storage_limit_gb,
    ephemeral_storage_usage_bytes,
  } = serverInstance
  const { ssh, extra } = metadata || {}

  // Use the custom hook to get volume details
  const volumeDetails = useStorageVolumeInfo(storage_volume_info)

  // Derived values
  const sshCommand = ssh
    ? `ssh ${ssh.username}@${ssh.ip} -p ${String(ssh.port)}`
    : 'SSH information not available'

  // Transform service_urls into a more structured format
  const serviceUrls: ServiceUrlInfo[] = (service_urls || []).map(
    (service: ServerServiceURL) => {
      const isJupyter = service.name === 'jupyter-labs'
      const fullUrl = isJupyter
        ? `${service.url}?token=${metadata?.extra?.notebook_token || ''}`
        : service.url

      return {
        name: service.name,
        url: service.url,
        fullUrl,
        mappedPort: service.mapped_port,
      }
    }
  )

  const onStartRemoteAccessClick = () => {
    getServerWebSSHSession.mutate(
      { instance_id: serverId },
      {
        onSuccess: (url) => {
          window.open(url, '_blank')
        },
        onError: () => {
          toast.error('Failed to start remote access')
        },
      }
    )
  }

  return (
    <div className="space-y-6">
      <InfoSection
        label="Execution Details"
        content={
          serverInstance.terminal_reason ? (
            <ExecutionDetails
              terminal_reason={serverInstance.terminal_reason}
              terminal_message={serverInstance.terminal_message}
              restart_count={serverInstance.restart_count}
              terminal_exit_code={serverInstance.terminal_exit_code}
              terminal_finished_at={serverInstance.terminal_finished_at}
            />
          ) : (
            <div className="flex items-center gap-3 py-3 px-4 rounded-lg bg-primary/5 border border-primary/10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-primary">
                  Running
                </span>
              </div>
            </div>
          )
        }
      />

      <InfoSection
        label="Specifications"
        content={
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
            <SpecCard
              icon={<Fan className="h-5 w-5" />}
              title="GPU"
              items={[
                { label: 'Count', value: `${gpu_request_count ?? 0}x` },
                { label: 'VRAM', value: `${gpu_request_gmem ?? 0} GB` },
              ]}
              variant="gpu"
            />

            <SpecCard
              icon={<Microchip className="h-5 w-5" />}
              title="CPU"
              items={[
                {
                  label: 'Count',
                  value: `${cpu_limit_count ?? 0}x`,
                },
              ]}
            />

            <SpecCard
              icon={<Microchip className="h-5 w-5 opacity-50" />}
              title="Memory"
              items={[
                {
                  label: 'RAM',
                  value: `${ram_limit_gb ?? 0} GB`,
                },
              ]}
            />

            <SpecCard
              icon={<HardDrive className="h-5 w-5" />}
              title="Storage"
              items={[
                {
                  label: (
                    <div className="flex items-center gap-1">
                      Ephemeral
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoCircledIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] p-3">
                            <p>
                              This storage is ephemeral, data will be deleted
                              along with the server if it is destroyed, if you
                              want permanent storage please create and attach a
                              storage volume
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ),
                  value:
                    ephemeral_storage_usage_bytes != null &&
                    ephemeral_storage_usage_bytes > 0
                      ? `${formatStorageBytes(ephemeral_storage_usage_bytes)} / ${ephemeral_storage_limit_gb ?? 0} GB`
                      : `${ephemeral_storage_limit_gb ?? 0} GB`,
                },
              ]}
            />
          </div>
        }
      />

      <Tabs
        value={resolvedActiveTab}
        onValueChange={(value) => {
          // Prevent navigation to autoscaling if not supported
          if (value === 'autoscaling' && !template?.support_scale) {
            return
          }
          onTabChange?.(value as 'overview' | 'autoscaling' | 'logs')
        }}
        className="w-full sm:w-auto"
      >
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {template?.support_scale && (
              <TabsTrigger value="autoscaling">Autoscaling</TabsTrigger>
            )}
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6">
          <InfoSection
            label="SSH Access"
            content={
              <div className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  {/* SSH Command Card */}
                  <div className="rounded-lg border bg-card p-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full ${ssh ? 'bg-primary/10' : 'bg-muted/50'}`}
                        >
                          <Terminal
                            className={`h-5 w-5 ${ssh ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground">
                            SSH Access
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            Connect directly via command line
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {ssh ? (
                          <div className="rounded-md border bg-muted/30 p-4">
                            <div className="mb-3 flex items-center justify-between">
                              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                SSH Command
                              </span>
                              <CopyButton
                                textToCopy={sshCommand}
                                icon={<CustomCopyIcon className="h-4 w-4" />}
                                className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3 rounded-md text-xs"
                              >
                                Copy
                              </CopyButton>
                            </div>
                            <code className="block break-all font-mono text-sm text-foreground leading-relaxed">
                              {sshCommand}{' '}
                              <span className="text-muted-foreground">
                                -i &lt;ssh_key_path&gt;
                              </span>
                            </code>
                          </div>
                        ) : (
                          <div className="rounded-md bg-muted/50 p-4 text-center">
                            <p className="text-xs text-muted-foreground">
                              Direct SSH access is not enabled for this server
                              instance.
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Please enable in advanced options when creating
                              the server.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Web SSH Card */}
                  <div className="rounded-lg border bg-card p-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full ${!serverInstance.enable_scale ? 'bg-primary/10' : 'bg-muted/50'}`}
                        >
                          <Terminal
                            className={`h-5 w-5 ${!serverInstance.enable_scale ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-foreground">
                              Browser Terminal
                            </h4>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <InfoCircledIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[300px] p-3">
                                  <p className="text-sm">
                                    <span className="font-medium">SSHX</span>{' '}
                                    lets you share your terminal with anyone by
                                    link, on a multiplayer infinite canvas.
                                  </p>
                                  <p className="text-sm mt-2">
                                    It has real-time collaboration, with remote
                                    cursors and chat. It's also fast and
                                    end-to-end encrypted, with a lightweight
                                    server written in Rust.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Access via web interface • Powered by sshx
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="rounded-md bg-secondary/10 p-4 text-center">
                          <p className="mb-3 text-xs text-muted-foreground">
                            Launch a web-based terminal session that runs
                            directly in your browser
                          </p>

                          {!serverInstance.enable_scale ? (
                            <Button
                              onClick={onStartRemoteAccessClick}
                              disabled={getServerWebSSHSession.isPending}
                              className="w-full"
                              size="sm"
                            >
                              {getServerWebSSHSession.isPending ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Starting Session...
                                </>
                              ) : (
                                <>
                                  <Terminal className="mr-2 h-4 w-4" />
                                  Launch Web Terminal
                                </>
                              )}
                            </Button>
                          ) : (
                            <div className="rounded-md bg-muted/50 p-3">
                              <p className="text-xs text-muted-foreground">
                                Web SSH is not available for autoscaling
                                instances
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Storage Mount
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setMountStorageDialogOpen(true)}
                      disabled={!template?.support_pvc}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Mount Storage
                    </Button>
                  </span>
                </TooltipTrigger>
                {!template?.support_pvc && (
                  <TooltipContent>
                    This server template does not support mounting storage volumes
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
            <div className="w-full">
              {!storage_volume_info?.length ? (
                <div className="rounded-lg border bg-card p-6 text-center">
                  <HardDrive className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    No persistent storage volumes are currently attached
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Mount storage volumes to preserve data between server restarts
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 lg:grid-cols-2">
                    {volumeDetails.map((volume) => (
                      <div
                        key={volume.id}
                        className="rounded-lg border bg-card p-6"
                      >
                        {volume.isLoading ? (
                          <div className="space-y-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                <Loader2 className="h-5 w-5 text-primary animate-spin" />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-foreground">
                                  Loading Volume
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  Fetching volume details...
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                <HardDrive className="h-5 w-5 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-foreground truncate">
                                  {volume.volume_name ||
                                    `Volume ${volume.storage_volume_id}`}
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  Persistent storage volume
                                  {volume.size_gb && ` • ${volume.size_gb} GB`}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div className="rounded-md border bg-muted/30 p-4">
                                <div className="mb-3">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Mount Details
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">
                                      Mount Path:
                                    </span>
                                    <code className="text-xs font-mono text-foreground bg-background/50 px-2 py-1 rounded">
                                      {volume.mount_path}
                                    </code>
                                  </div>
                                </div>
                              </div>

                              {volume.google_drive_metadata &&
                                volume.google_drive_metadata.length > 0 && (
                                  <div className="rounded-md bg-secondary/10 p-4">
                                    <div className="mb-3 flex items-center justify-between">
                                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Cloud Integration
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <img
                                          src={GoogleDriveLogo}
                                          alt="Google Drive"
                                          className="w-4 h-4"
                                        />
                                        <span className="text-xs text-foreground">
                                          Google Drive Connected
                                        </span>
                                      </div>
                                      <GoogleDriveDetails
                                        TriggerButton={
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-3 text-xs"
                                          >
                                            View Details
                                          </Button>
                                        }
                                        googleDriveMetadata={
                                          volume.google_drive_metadata
                                        }
                                      />
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <InfoSection
            label="HTTP Ports"
            content={
              serviceUrls.length ? (
                <div className="space-y-3 rounded-lg border bg-card p-4">
                  {serviceUrls.map((service) => (
                    <ServiceUrlItem
                      key={service.mappedPort}
                      service={service}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">Not available</p>
              )
            }
          />

          <InfoSection
            label="Extra"
            content={
              extra && Object.keys(extra).length ? (
                <div className="rounded-lg border bg-card p-4 space-y-2">
                  {Object.entries(extra).map(([key, value]) => (
                    <p key={key} className="text-sm">
                      <span className="font-medium text-muted-foreground">
                        {key}:
                      </span>{' '}
                      <span className="text-foreground">{String(value)}</span>
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No extra data</p>
              )
            }
          />
        </TabsContent>

        <TabsContent value="autoscaling">
          {!serverInstance.enable_scale ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">
                Autoscaling Not Enabled
              </h4>
              <p className="text-sm text-muted-foreground max-w-md">
                This server instance doesn't have autoscaling enabled.
                Autoscaling allows automatic scaling based on traffic demand.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <AutoscalingConfigCard
                config={currentServerInstance.instance_scale_config}
                replicaStatus={currentServerInstance.replica_status}
                lastUpdated={lastUpdated}
                onManualRefresh={() => manualRefetch()}
                isRefetching={isRefetching}
              />
              <AutoscalingHistory serverId={serverId} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="logs">
          <ServerLogs
            instanceId={serverId}
            containerName={
              serverInstance.metadata?.extra?.container_name as string
            }
          />
        </TabsContent>
      </Tabs>

      <MountStorageDialog
        open={mountStorageDialogOpen}
        onOpenChange={setMountStorageDialogOpen}
        serverInstance={currentServerInstance}
        serverId={serverId}
        supportsPVC={template?.support_pvc}
        storageRequired={template?.storage_required}
      />
    </div>
  )
}

const timeRangeOptions = [
  { value: '1h', label: 'Past hour', amount: 1, unit: 'hour' },
  { value: '24h', label: 'Past day', amount: 1, unit: 'day' },
  { value: '7d', label: 'Past week', amount: 7, unit: 'day' },
  { value: '30d', label: 'Past month', amount: 1, unit: 'month' },
  // { value: '3m', label: 'Past 3 months', amount: 3, unit: 'month' },
]

const AutoscalingHistory = ({ serverId }: { serverId: number }) => {
  const [timeRange, setTimeRange] = useState('24h')

  const { now, startAt } = useMemo(() => {
    const now = dayjs()
    const selectedOption =
      timeRangeOptions.find((option) => option.value === timeRange) ||
      timeRangeOptions[1] // Default to 'Past day'

    const startAt = now
      .subtract(
        selectedOption.amount,
        selectedOption.unit as dayjs.ManipulateType
      )
      .unix()

    return { now: now.unix(), startAt }
  }, [timeRange])

  const {
    data: historyData,
    isLoading,
    isError,
    error,
  } = useGetServerInstanceScaleHistory({
    instanceId: serverId,
    startAt: startAt,
    endAt: now,
    unit: timeRange === '1h' ? 'minute' : 'hour',
    enabled: !!serverId,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Activity className="h-12 w-12 text-destructive/50 mb-4" />
        <h4 className="text-lg font-medium text-foreground mb-2">
          Error Loading History
        </h4>
        <p className="text-sm text-muted-foreground max-w-md">
          There was a problem fetching the scaling history. Please try again
          later.
        </p>
        {error && (
          <p className="mt-2 text-xs text-destructive/80 bg-destructive/10 p-2 rounded-md">
            {JSON.stringify(error)}
          </p>
        )}
      </div>
    )
  }

  if (!historyData || historyData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h4 className="text-lg font-medium text-foreground mb-2">
          No Scaling History
        </h4>
        <p className="text-sm text-muted-foreground max-w-md">
          No scaling history is available for the selected time range.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Header */}
      <div className="bg-primary/5 px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-foreground/60">Analytics</p>
              <p className="text-xs text-muted-foreground">
                Replica count changes over the selected time period (
                {historyData.length} data points)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="p-6">
        <ScaleHistoryChart data={historyData} timeRange={timeRange} />
      </div>
    </div>
  )
}

// Child components
const AutoscalingConfigCard = ({
  config,
  replicaStatus,
  lastUpdated,
  onManualRefresh,
  isRefetching,
}: {
  config: GetServerInstanceData['instance_scale_config']
  replicaStatus?: GetServerInstanceData['replica_status']
  lastUpdated?: Date | null
  onManualRefresh?: () => void
  isRefetching?: boolean
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const updateConfig = useUpdateServerInstanceScaleConfig()

  const formSchema = z.object({
    enabledScale: z.boolean(),
    pollingInterval: z.coerce.number().min(1, 'Must be at least 1 second'),
    cooldownPeriod: z.coerce.number().min(1, 'Must be at least 1 second'),
    minReplicaCount: z.coerce.number().min(1, 'Must be at least 1'),
    maxReplicaCount: z.coerce.number().min(1, 'Must be at least 1'),
  })

  const form = useForm<AutoscalingFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      enabledScale: true,
      pollingInterval: config?.polling_interval || 30,
      cooldownPeriod: config?.cooldown_period || 300,
      minReplicaCount: config?.min_replica_count || 1,
      maxReplicaCount: config?.max_replica_count || 5,
    },
  })

  const handleEdit = () => {
    if (config) {
      form.reset({
        enabledScale: true,
        pollingInterval: config.polling_interval,
        cooldownPeriod: config.cooldown_period,
        minReplicaCount: config.min_replica_count,
        maxReplicaCount: config.max_replica_count,
      })
    }
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    form.reset()
  }

  const handleSave = async (values: AutoscalingFormData) => {
    if (!config) return

    try {
      // Zod coercion ensures these are numbers after validation
      await updateConfig.mutateAsync({
        config_id: config.id,
        config: {
          polling_interval: values.pollingInterval as number,
          cooldown_period: values.cooldownPeriod as number,
          min_replica_count: values.minReplicaCount as number,
          max_replica_count: values.maxReplicaCount as number,
        },
      })

      setIsEditing(false)
      toast.success('Autoscaling configuration updated successfully')
    } catch (error) {
      toast.error('Failed to update autoscaling configuration')
    }
  }

  if (!config) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-primary" />
          <span className="font-semibold text-foreground">Enabled</span>
        </div>
        <p className="text-sm text-foreground/60">
          Configuration details not available
        </p>
      </div>
    )
  }

  const metrics = [
    {
      label: 'Polling Interval',
      value: `${config.polling_interval}s`,
      description: 'How often the system checks for scaling needs',
    },
    {
      label: 'Cooldown Period',
      value: `${config.cooldown_period}s`,
      description: 'Wait time between scaling operations',
    },
  ]

  if (isEditing) {
    return (
      <div className="rounded-lg border bg-card overflow-hidden">
        {/* Header */}
        <div className="bg-primary/5 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-foreground/60">
                  Edit Autoscaling Configuration
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="h-8 px-3"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={form.handleSubmit(handleSave)}
                disabled={updateConfig.isPending}
                className="h-8 px-3"
              >
                {updateConfig.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="p-6">
          <Form {...form}>
            <form className="space-y-4">
              <AutoscalingConfigForm
                form={form}
                isEnabled={true}
                compact={true}
                showToggle={false}
              />
            </form>
          </Form>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Header */}
      <div className="bg-primary/5 px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-foreground/60">
                Dynamic scaling based on traffic
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            className="h-8 px-3"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            Edit Configuration
          </Button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="p-6">
        {/* Combined Replica Status & Scale Range */}
        {replicaStatus && (
          <div className="p-6 rounded-lg bg-background/50 border border-border/50">
            {/* Header with Live Status */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-foreground/70">
                  Status
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs text-foreground/50">Live</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {lastUpdated && (
                  <span className="text-xs text-foreground/40">
                    Updated{' '}
                    {Math.floor((Date.now() - lastUpdated.getTime()) / 1000)}{' '}
                    seconds ago
                  </span>
                )}
                {onManualRefresh && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onManualRefresh}
                    disabled={isRefetching}
                    className="h-6 w-6 p-0 hover:bg-primary/10"
                  >
                    <RefreshCw
                      className={cn(
                        'h-3 w-3 text-foreground/60 hover:text-primary transition-colors',
                        isRefetching && 'animate-spin'
                      )}
                    />
                  </Button>
                )}
              </div>
            </div>

            {/* Main Capacity Display */}
            <div className="space-y-4">
              {/* Current vs Max Capacity */}
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-primary">
                    {replicaStatus.replicas}
                  </span>
                  <span className="text-sm text-foreground/60">
                    / {config.max_replica_count} replicas
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm text-foreground/80">
                    {replicaStatus.ready_replicas} ready
                  </div>
                  <div className="text-xs text-foreground/50">
                    {replicaStatus.unavailable_replicas > 0 &&
                      `${replicaStatus.unavailable_replicas} unavailable`}
                  </div>
                </div>
              </div>

              {/* Capacity Bar - Current vs Max */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-foreground/50">
                  <span>Capacity Usage</span>
                  <span>
                    {Math.round(
                      (replicaStatus.replicas / config.max_replica_count) * 100
                    )}
                    %
                  </span>
                </div>
                <div className="relative w-full h-3 bg-muted rounded-full overflow-hidden">
                  {/* Min threshold marker */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="absolute top-0 h-full w-1 bg-amber-400 shadow-sm z-10 cursor-help hover:w-3 hover:-translate-x-1 hover:shadow-md transition-all duration-200"
                          style={{
                            left: `${(config.min_replica_count / config.max_replica_count) * 100}%`,
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[280px] p-3 bg-primary/75">
                        <p className="text-sm">
                          <span className="font-medium">
                            Minimum Threshold:
                          </span>{' '}
                          The system will maintain at least{' '}
                          {config.min_replica_count} replica
                          {config.min_replica_count !== 1 ? 's' : ''} even
                          during low traffic periods to ensure availability.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {/* Current capacity */}
                  <div
                    className="h-full bg-gradient-to-r from-primary/80 to-primary transition-all duration-500"
                    style={{
                      width: `${(replicaStatus.replicas / config.max_replica_count) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-foreground/60">
                  <span>Min: {config.min_replica_count}</span>
                  <span>Max: {config.max_replica_count}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 mt-6 ">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="group relative p-4 rounded-lg bg-background/50 border border-border/50 hover:border-primary/20 hover:bg-primary/5 transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs font-medium text-foreground/70 uppercase tracking-wide">
                      {metric.label}
                    </p>
                    <div className="px-2 py-1 rounded-md bg-primary/10 text-primary text-sm font-bold">
                      {metric.value}
                    </div>
                  </div>
                  <p className="text-xs text-foreground/50 leading-relaxed">
                    {metric.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const InfoSection = ({ label, content }: InfoSectionProps) => (
  <div className="space-y-2">
    <h3 className="text-base font-semibold text-foreground">{label}</h3>
    <div className="w-full">{content}</div>
  </div>
)

const formatStorageBytes = (bytes: number): string => {
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(0)} MB`
  const kb = bytes / 1024
  return `${kb.toFixed(0)} KB`
}

const ServiceUrlItem = ({ service }: { service: ServiceUrlInfo }) => (
  <div className="flex items-center gap-3">
    <Badge className="bg-primary/15 text-primary hover:bg-primary/20 rounded-lg px-3 py-1.5 text-sm font-medium">
      :{service.mappedPort}
    </Badge>
    <span className="text-sm font-medium text-foreground">{service.name}</span>
    <div className="flex-1 font-mono text-sm px-3 py-2 rounded-md bg-muted/50 dark:bg-muted/30 border">
      <a
        href={service.fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all"
      >
        {service.fullUrl}
      </a>
    </div>
    <CopyButton
      textToCopy={service.fullUrl}
      className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3 rounded-md text-sm"
    >
      Copy
    </CopyButton>
  </div>
)
